import { invariant } from "@olenbetong/appframe-core";

import { getDefaultClient, type IClient } from "../Client.js";
import type { DataHandler } from "../DataHandler.js";
import { EventTarget } from "../events.js";

export type UploadTaskStatus = "pending" | "uploading" | "completed" | "failed" | "aborted";

export type UploadTaskEventToValueMap = {
	onStart: null;
	onAbort: null;
	onStatus: UploadTaskStatus;
	onProgress: {
		loaded: number;
		total: number;
	};
};

export type UploadTaskEvent = keyof UploadTaskEventToValueMap;

export const UploadTaskEvents: Record<UploadTaskEvent, { abortable: boolean }> = {
	onStart: { abortable: false },
	onAbort: { abortable: false },
	onStatus: { abortable: false },
	onProgress: { abortable: false },
};

export type UploadTaskOptions = {
	client?: IClient | null;
	/**
	 * Explicit upload URL. If omitted, a DataHandler with `getUploadUrl` must
	 * be provided.
	 */
	url?: string;
	/**
	 * DataHandler used to generate upload URL when `url` is not provided.
	 */
	dataHandler?: DataHandler<any>;
	/**
	 * Name of field to upload to when uploading blobs.
	 */
	fieldName?: string;
	/**
	 * Primary key for the record we're uploading to.
	 */
	primKey?: string;
	file: File | Blob;
	fields?: Record<string, any>;
	fileName?: string;
};

export type UploadUrlOptions = Omit<UploadTaskOptions, "file" | "url" | "dataHandler">;

export class UploadTask extends EventTarget<UploadTaskEventToValueMap> {
	protected options: UploadTaskOptions;
	protected request: XMLHttpRequest | AbortController | null;
	protected promise: Promise<unknown> | null;
	protected rejectFunc: ((reason?: any) => void) | null;
	protected status: UploadTaskStatus;
	protected progress: { loaded: number; total: number };
	get client(): IClient {
		return this.options.client ?? getDefaultClient();
	}
	set client(value: IClient | null) {
		this.options.client = value;
	}

	constructor(options: UploadTaskOptions) {
		super(UploadTaskEvents);
		this.options = options;
		this.request = null;
		this.promise = null;
		this.rejectFunc = null;
		this.status = "pending";
		let total = (options.file as File).size ?? 0;
		this.progress = { loaded: 0, total };
	}

	getStatus() {
		return this.status;
	}

	getProgress() {
		return this.progress;
	}

	async start() {
		if (this.status !== "pending") {
			return this.promise;
		}

		this.status = "uploading";
		this.fireEvent("onStart", null);
		this.fireEvent("onStatus", this.status);

		// A PrimKey among the fields targets that record: it goes into the URL, not the form data.
		if (this.options.fields?.["PrimKey"]) {
			this.options.primKey = this.options.fields.PrimKey;
			delete this.options.fields.PrimKey;
		}

		const { file, fields, fileName, dataHandler, url: explicitUrl, fieldName, primKey } = this.options;

		const allFields = {
			...fields,
		} as Record<string, any>;

		const uploadField = fieldName ?? "File";
		const targetUrl = explicitUrl ?? dataHandler?.getUploadUrl?.({ fieldName, primKey, fields }) ?? "";

		invariant(targetUrl, "Upload URL not provided");

		if (!globalThis.XMLHttpRequest || !globalThis.FormData) {
			throw Error(
				"UploadTask requires browser APIs. Use the node upload utilities from '@olenbetong/appframe-data/node'.",
			);
		}

		const data = new globalThis.FormData();

		for (let [field, value] of Object.entries(allFields)) {
			data.append(field, value);
		}

		data.append(uploadField, file, file instanceof File ? file.name : fileName || "file");

		this.promise = new Promise((resolve, reject) => {
			this.rejectFunc = reject;
			const request = new XMLHttpRequest();
			this.request = request;

			request.upload.addEventListener("progress", (evt) => {
				this.progress = { loaded: evt.loaded, total: evt.total };
				this.fireEvent("onProgress", this.progress);
			});

			request.addEventListener("readystatechange", () => {
				if (request.readyState === 4) {
					if (request.status === 200) {
						this.status = "completed";
						this.fireEvent("onStatus", this.status);
						resolve(request.response);
					} else {
						try {
							const result = JSON.parse(request.response);
							invariant(!result.error, result.error);
							throw Error(`Upload failed: ${request.status} ${request.response}`);
						} catch (error) {
							this.status = "failed";
							this.fireEvent("onStatus", this.status);
							reject(error);
						}
					}
				}
			});

			request.open("POST", targetUrl);
			request.setRequestHeader("Accept", "application/json");
			request.setRequestHeader("X-Requested-With", "XMLHttpRequest");
			request.send(data as FormData);
		});
		return this.promise;
	}

	abort() {
		if (this.status === "uploading" && this.request) {
			this.request.abort();
			this.status = "aborted";
			this.fireEvent("onAbort", null);
			this.fireEvent("onStatus", this.status);
			this.rejectFunc?.(new DOMException("Aborted", "AbortError"));
		}
	}
}
